/**
 * One Gap Management Quality row (the object Summary.tsx renders), built from the
 * shared measure math. Pinned field-for-field by __tests__/measure-math.parity.test.ts.
 */
import type { PracticeSize } from '@/lib/mips-scoring';
import {
  deriveMeasure,
  findExactBenchmark,
  scoreMeasure,
  type MeasureDataSource,
  type MeasureDraft,
  type MeasureSource,
  type SimRule,
} from './measure-math';

export interface RowMetaDataSource {
  getMeasureWithSubmissionMethod(measureId: string, submissionMethod: string): any;
  getMeasureMeta(measureId: string): any;
}

/** Display metadata for a row (Summary's metaForMethod). */
export function rowMetaFor(ds: RowMetaDataSource, measureId: string, submissionMethod?: string): any {
  if (submissionMethod) {
    const byMethod = ds.getMeasureWithSubmissionMethod(measureId, submissionMethod);
    if (byMethod) {
      return {
        collectionType: byMethod.submissionMethod,
        measureType: byMethod.measureType,
        inverse: byMethod.isInverse,
        autoPts: byMethod.autoPts,
        cmsAvg: byMethod.cmsAverage,
        cap7: byMethod.sevenPointCap,
        toppedOut: byMethod.toppedOut,
      };
    }
  }
  return ds.getMeasureMeta(measureId);
}

export interface GapRowContext {
  practiceSize: PracticeSize;
  simRule: SimRule;
  simDelta: number;
  ds: MeasureDataSource;
  /** Display metadata for the row (Summary's metaForMethod). Spread after `title`, as before. */
  meta: Record<string, any> | undefined;
}

export function buildGapRow(
  src: MeasureSource,
  compositeId: string,
  measureId: string,
  submissionMethod: string | undefined,
  draft: MeasureDraft | undefined,
  ctx: GapRowContext,
): any {
  const derived = deriveMeasure(src, measureId, submissionMethod, draft, ctx.ds);
  const scored = scoreMeasure(derived, {
    practiceSize: ctx.practiceSize,
    simRule: ctx.simRule,
    simDelta: ctx.simDelta,
    benchmark: findExactBenchmark(ctx.ds, measureId, submissionMethod),
  });
  const { prBase, dc } = derived;

  return {
    id: measureId,
    title: src.title,
    ...ctx.meta,
    ...derived.counts,
    dc,
    dcStr: `${(dc || 0).toFixed(2)}%`,
    pr: prBase,
    prStr: `${(prBase || 0).toFixed(2)}%`,
    points: scored.points,
    pointsImproved: scored.pointsImproved,
    potentialGain: scored.potentialGain,
    prImproved: scored.prImproved,
    isAdded: derived.isManualMode, // true in manual mode to enable input fields
    mode: derived.isManualMode ? ('manual' as const) : ('calc' as const),
    compositeId,
    isMultiStrata: src.isMultiStrata,
    strata: derived.displayStrata,
    overallAlgorithm: derived.algorithm,
    overallPr: prBase,
    overallDc: dc,
    submissionMethod: src.submissionMethod,
  };
}
